/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Modal,
  notification,
  Radio,
  Row,
  Space,
  TablePaginationConfig,
  TableProps,
  Upload,
  UploadFile,
  UploadProps,
} from 'antd';
import { UploadOutlined, DeleteFilled } from '@ant-design/icons';
import TableComponent from '@/components/table';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { getExcelData } from '@/helpers/excelHelper';
import { useMutation } from '@tanstack/react-query';
import { twMerge } from 'tailwind-merge';
import { isEmpty, isUndefined } from 'lodash';
import {
  ICollector,
  ICollectorItem,
  ISearchCollectorItemReq,
} from '@/interfaces/collector';
import { ECollectorType, EPaymentType } from '@/enums/ECollector';
import { formatNumber } from '@/utils/dataUtils';
import { validateIvs } from '@/adapters/order';
import locale from 'antd/es/date-picker/locale/th_TH';
import dayjs from 'dayjs';
import { addCollector } from '@/adapters/collector';
import { IMediaUpload } from '@/interfaces/media';
import { uploadMedia } from '@/adapters/media';

interface IProps {
  open: boolean;
  handleOk: () => void;
  handleCancel: () => void;
}

const _criteria: ISearchCollectorItemReq = {
  orderType: 'descend',
  pageSize: 20,
  pageStart: 1,
  search: '',
};

const ImportCoaModal = ({ open, handleOk, handleCancel }: IProps) => {
  const [criteria, setCriteria] = useState<ISearchCollectorItemReq>({
    ..._criteria,
  });
  const [items, setItems] = useState<ICollectorItem[]>([]);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [isValidate, setIsValidate] = useState<boolean>(false);
  const [form] = Form.useForm<Partial<ICollector>>();

  const collectorType: ECollectorType = Form.useWatch(
    'collectorType',
    form,
  ) as ECollectorType;
  const isBill = collectorType === ECollectorType.BILLING;
  const isPaymentIn = collectorType === ECollectorType.PAYMENT_IN;

  const onTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<ICollectorItem> | SorterResult<ICollectorItem>[],
  ) => {
    const newCriteria = { ...criteria };
    if (!Array.isArray(sorter)) {
      newCriteria.orderBy =
        (sorter.column?.dataIndex as keyof ICollectorItem) || '';
      newCriteria.orderType = sorter.order || '';
    }
    setCriteria({
      ...newCriteria,
      pageStart: Number(pagination.current || 1),
      pageSize: Number(pagination.pageSize || 10),
    });
  };

  const columns: TableProps<ICollectorItem>['columns'] = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
      width: 40,
      align: 'center',
      className: 'text-center',
      render: (_, rec, i) => <Space>{i + 1}</Space>,
    },
    {
      title: 'AWB',
      dataIndex: 'code',
      key: 'code',
      className: 'font-bold',
      width: 120,
      align: 'center',
    },
    {
      title: 'รหัสลูกค้า',
      dataIndex: 'customerCode',
      key: 'customerCode',
      className: 'font-bold',
      width: 120,
      align: 'center',
    },
    {
      title: 'ชื่อลูกค้า',
      dataIndex: 'customerName',
      key: 'customerName',
      className: 'font-bold',
    },
    {
      title: isBill ? 'เลขที่บิล' : 'เลขที่ CHEQUE',
      dataIndex: 'billingNo',
      key: 'billingNo',
      className: 'font-bold',
      width: 120,
      align: 'center',
    },
    {
      title: 'เลขที่ Invoice',
      dataIndex: 'ivNo',
      key: 'ivNo',
      className: 'font-bold',
      width: 120,
      align: 'center',
    },
    {
      title: isBill ? 'PAY DATE' : 'CHEQUE DATE',
      dataIndex: 'paidDateStr',
      key: 'paidDateStr',
      className: 'font-bold',
      width: 120,
      align: 'center',
    },
    {
      title: isBill ? 'ยอดวางบิล' : 'ยอดรับเช็ค',
      dataIndex: 'billingAmount',
      key: 'billingAmount',
      className: 'font-bold',
      width: 120,
      align: 'right',
      render: _ => formatNumber(_, 2),
    },
    {
      title: 'ยอด Invoice',
      dataIndex: 'paidAmount',
      key: 'paidAmount',
      className: 'font-bold',
      width: 120,
      align: 'right',
      render: _ => formatNumber(Number(_), 2),
    },
    {
      title: 'สถานะ',
      dataIndex: 'status',
      key: 'status',
      fixed: 'right',
      align: 'center',
      width: 80,
      className: 'font-bold',
      render: _ => (
        <Space
          className={twMerge(
            _ === 2 && 'text-red-600',
            _ === 3 && 'text-green-700',
          )}
        >
          {_ === 1 ? 'Pending' : _ === 2 ? 'Error' : 'Success'}
        </Space>
      ),
    },
    {
      title: 'Remark',
      dataIndex: 'remark',
      key: 'remark',
      className: 'font-bold',
      width: 120,
    },
    {
      title: '',
      dataIndex: 'action',
      key: 'action',
      fixed: 'right',
      align: 'center',
      width: 40,
      className: 'font-bold',
      render: (_, rec) => (
        <DeleteFilled
          className="text-red-700"
          onClick={() => setItems(c => c.filter(x => x.ivNo !== rec.ivNo))}
        />
      ),
    },
  ];

  const props: UploadProps = {
    onRemove: () => {
      setFileList([]);
      setItems([]);
    },
    beforeUpload: async file => {
      setFileList([file]);
      if (!isPaymentIn) {
        let excelData: any[] = (await getExcelData(file)) as any[];
        excelData = excelData
          .slice(1)
          .filter(c => !isEmpty(c[0]) || !isUndefined(c[0]))
          .map((c: any) => {
            const item: ICollectorItem = {
              code: c[0].toString(),
              customerCode: c[2].toString(),
              customerName: c[3].toString(),
              billingNo: c[4].toString(),
              billingAmount: isBill ? c[6] : c[8],
              paidDate: c[5].toString(),
              paidDateStr: c[5].toString(),
              ivNo: isBill ? c[7].toString() : c[10].toString(),
              paidAmount: isBill ? c[9] : c[11],
              status: 1,
              remark: isBill ? c[14] || '' : '',
            } as ICollectorItem;
            return item;
          });
        setItems(excelData);
      }
    },
    fileList,
    showUploadList: true,
    accept: '.xlsx',
  };

  const mutationAddCollector = useMutation({
    mutationFn: async (collectorData: ICollector) =>
      await addCollector(collectorData),
    mutationKey: ['add-collector'],
    onSuccess: data => {
      if (data.status === 200) {
        setFileList([]);
        setItems([]);
        setIsValidate(false);
        notification.success({
          message: 'นำเข้าข้อมูลสำเร็จ',
        });
        handleOk();
      } else {
        notification.error({
          message: 'นำเข้าข้อมูลไม่สำเร็จ',
        });
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  const validateInvMutations = useMutation({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    mutationFn: async (collectorData: ICollector) =>
      await validateIvs(
        items?.map(i => {
          return i.ivNo;
        }),
      ),
    mutationKey: ['valitdate-ins'],
    onSuccess: async (data, collectorReq) => {
      if (data.status === 200) {
        const mediaUploadFile = {
          file: fileList[0] as any,
          modelId: 1,
          modelType: 'collector',
        } as Partial<IMediaUpload>;
        const uploadData = await uploadMedia(mediaUploadFile);
        if (uploadData?.data?.id) {
          await mutationAddCollector.mutateAsync({
            ...collectorReq,
            mediaId: uploadData?.data?.id,
          });
        }
      } else {
        const noneExists: string[] = data.data?.data as string[];
        const updateItems = items.map(c => {
          c.status = noneExists.includes(c.ivNo ?? '') ? 2 : 3;
          return c;
        });
        setItems(updateItems);
        setIsValidate(true);
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  const handleUpdate = async (isConfirm: boolean) => {
    form
      .validateFields()
      .then(async (values: Partial<ICollector>) => {
        if (!isPaymentIn && !items.length) {
          notification.warning({
            message: 'กรุณาเลือกที่ไฟล์ที่ต้องการ',
          });
        }
        const updateItems = [...items].map(i => {
          i.paidDate = dayjs(i.paidDateStr, 'DDMMYYYY').toDate();
          i.billingDate = dayjs(values.collectorDate as any).toDate();
          i.paymentType = isBill
            ? i.remark.toLowerCase().includes('tt')
              ? EPaymentType.BANK_TRANSFER
              : EPaymentType.CHEQUE
            : EPaymentType.CHEQUE;
          return i;
        });
        const collectorData: ICollector = {
          ...values,
          collectorDate: dayjs(values.collectorDate as any).toDate(),
          collectorItems: updateItems,
          isConfirm: isConfirm,
          remark: fileList[0].name,
        } as ICollector;
        validateInvMutations.mutateAsync(collectorData);
      })
      .catch(info => {
        console.log('Validate Failed:', info);
      });
  };

  const handleUpdatePassError = async (isConfirm: boolean) => {
    form
      .validateFields()
      .then(async (values: Partial<ICollector>) => {
        if (!isPaymentIn && !items.length) {
          notification.warning({
            message: 'กรุณาเลือกที่ไฟล์ที่ต้องการ',
          });
        }
        const updateItems = [...items].map(i => {
          i.paidDate = dayjs(i.paidDateStr, 'DDMMYYYY').toDate();
          i.billingDate = dayjs(values.collectorDate as any).toDate();
          i.paymentType = isBill
            ? i.remark.toLocaleLowerCase() === 'tt'
              ? EPaymentType.BANK_TRANSFER
              : EPaymentType.CHEQUE
            : EPaymentType.CHEQUE;
          return i;
        });
        const collectorData: ICollector = {
          ...values,
          collectorDate: dayjs(values.collectorDate as any).toDate(),
          collectorItems: updateItems,
          isConfirm: isConfirm,
          remark: fileList[0].name,
        } as ICollector;
        const mediaUploadFile = {
          file: fileList[0] as any,
          modelId: 1,
          modelType: 'collector',
        } as Partial<IMediaUpload>;
        const uploadData = await uploadMedia(mediaUploadFile);
        if (uploadData?.data?.id) {
          await mutationAddCollector.mutateAsync({
            ...collectorData,
            mediaId: uploadData?.data?.id,
          });
        }
      })
      .catch(info => {
        console.log('Validate Failed:', info);
      });
  };

  return (
    <Modal
      title="นำเข้าไฟล์"
      open={open}
      width={'1200px'}
      onOk={() => {
        handleOk();
      }}
      onCancel={handleCancel}
      okText="บันทึก"
      cancelText="ยกเลิก"
      destroyOnClose
      footer={[
        <Button
          key="submit"
          type="primary"
          form="unit"
          className=" !hover:"
          onClick={() => handleUpdate(true)}
          loading={
            validateInvMutations.isPending || mutationAddCollector.isPending
          }
        >
          Validate
        </Button>,
        isValidate && (
          <Button
            ghost
            key="submit"
            type="primary"
            form="unit"
            className=" !hover:"
            onClick={() => handleUpdatePassError(true)}
            loading={
              validateInvMutations.isPending || mutationAddCollector.isPending
            }
          >
            บันทึก
          </Button>
        ),
      ]}
    >
      <div className="flex flex-col gap-2 w-full">
        <Form
          form={form}
          name="collectorModal"
          layout="vertical"
          className="w-full"
        >
          <Row gutter={12}>
            <Col xs={24} md={6}>
              <Form.Item<Partial<ICollector>>
                label="วันที่นำเข้า"
                name="collectorDate"
                rules={[{ required: true, message: 'วันที่นำเข้า' }]}
                initialValue={dayjs()}
              >
                <DatePicker size="large" locale={locale} />
              </Form.Item>
            </Col>
            <Col xs={24} md={10}>
              <Form.Item<ICollector>
                label="ประเภทไฟล์"
                name="collectorType"
                rules={[{ required: true, message: 'เลือกประเภทไฟล์' }]}
                initialValue={ECollectorType.BILLING}
              >
                <Radio.Group
                  onChange={() => {
                    setFileList([]);
                    setItems([]);
                  }}
                >
                  <Radio value={ECollectorType.BILLING}>รายการวางบิล</Radio>
                  <Radio value={ECollectorType.PAYMENT_RECEIVED}>
                    รายการรับเช็ค
                  </Radio>
                  <Radio value={ECollectorType.PAYMENT_IN}>เข้าเช็ค</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="อัพโหลดไฟล์" name="file">
                <Upload {...props}>
                  {fileList.length === 0 && (
                    <Button icon={<UploadOutlined />}>เลือกไฟล์</Button>
                  )}
                </Upload>
              </Form.Item>
            </Col>
          </Row>
        </Form>
        {!isPaymentIn && (
          <TableComponent<ICollectorItem>
            columns={columns}
            criteria={criteria}
            data={{
              data: items,
              totalRecords: items.length,
            }}
            loading={false}
            onTableChange={onTableChange}
            scroll
            scrollLength={1400}
          />
        )}
      </div>
    </Modal>
  );
};

export default ImportCoaModal;
